import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Keeps open tabs on the latest release. A tab opened before a deploy keeps
// running the old code until a full reload, so new options (e.g. a new FIDA
// clinic) or fixes don't appear and users can't tell why. This checks
// index.html for a new release (on load, every few minutes and when the tab
// is shown again) and, once one is out, reloads on the user's next page
// change, so nothing they are typing is lost.

const CHECK_EVERY_MS = 5 * 60 * 1000;
const MAIN_BUNDLE = /\/static\/js\/main\.[a-f0-9]+\.js/;

// The release this tab is running: its main bundle's file name.
export const runningRelease = (doc = document) => {
  const script = Array.from(doc.querySelectorAll("script[src]")).find((s) =>
    MAIN_BUNDLE.test(s.getAttribute("src"))
  );
  return script ? script.getAttribute("src").match(MAIN_BUNDLE)[0] : null;
};

// The release the server is serving now (null if it can't tell).
export const latestRelease = async () => {
  try {
    const res = await fetch(`/index.html?release-check=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const match = (await res.text()).match(MAIN_BUNDLE);
    return match ? match[0] : null;
  } catch {
    return null;
  }
};

export const useReloadOnNewRelease = () => {
  const location = useLocation();
  const newReleaseOut = useRef(false);
  const firstRoute = useRef(true);

  useEffect(() => {
    const running = runningRelease();
    // Development builds have no hashed bundle: nothing to compare.
    if (!running) return undefined;
    const check = async () => {
      const latest = await latestRelease();
      if (latest && latest !== running) newReleaseOut.current = true;
    };
    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    if (newReleaseOut.current) window.location.reload();
  }, [location.pathname]);
};
